import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Drawer, Button, Pagination, AgGridTable } from 'components/common';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { FiDownload } from 'react-icons/fi';
import apiAxios from 'helpers/apiAxios';
import { ExportToExcel } from 'helpers/utils';

const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const fmtDate = (iso) => {
  if (!iso) return '—';
  const p = String(iso).split('-');
  if (p.length !== 3) return iso;
  return `${p[2].padStart(2,'0')}-${MONTHS_SHORT[parseInt(p[1],10)-1]}-${p[0]}`;
};

const fmtM = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
};
const fmtW = (n) => `${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} MT`;

const PAGE_SIZE = 30;

const CONTRACT_ENDPOINT = '/crm/dashboard/contracts/get_detailed_data';
const ADDENDUM_ENDPOINT = '/crm/dashboard/contracts/get_addendum_data';

const ContractDrillDownPanel = ({ open, title, baseParams, extraParams, onClose, mode }) => {
  const isAddendum = mode === 'addendums';
  const endpoint   = isAddendum ? ADDENDUM_ENDPOINT : CONTRACT_ENDPOINT;

  const [records,      setRecords]      = useState([]);
  const [totals,       setTotals]       = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [exporting,    setExporting]    = useState(false);
  const [page,         setPage]         = useState(1);
  const [totalPages,   setTotalPages]   = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const fetchRecords = useCallback(async (pg) => {
    setLoading(true);
    try {
      const res = await apiAxios.get(endpoint, {
        params: { ...baseParams, ...extraParams, pagination: { page: pg, per_page: PAGE_SIZE } }
      });
      setRecords(res.data?.data || []);
      setTotalPages(res.data?.pagination?.total_pages || 1);
      setTotalRecords(res.data?.pagination?.total_records || 0);
      setTotals(res.data?.totals || null);
    } catch {
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [baseParams, extraParams, endpoint]);

  useEffect(() => {
    if (!open) return;
    setPage(1);
    fetchRecords(1);
  }, [open, fetchRecords]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const allPages = [];
      let p = 1;
      let total = 1;
      do {
        const res = await apiAxios.get(endpoint, {
          params: { ...baseParams, ...extraParams, pagination: { page: p, per_page: 200 } }
        });
        const rows = res.data?.data || [];
        allPages.push(...rows);
        total = res.data?.pagination?.total_pages || 1;
        p += 1;
      } while (p <= total);

      const exportRows = isAddendum
        ? allPages.map(r => ({
            'J Number':            r.j_number || r.q_number || '',
            'Contract #':          r.contract_number || '',
            'Addendum #':          r.addendum_number || '',
            'Addendum Date':       r.addendum_date || '',
            'Contract Date':       r.contract_date || '',
            'Project':             r.project_name || '',
            'Customer':            r.customer_name || '',
            'Addendum Value':      r.addendum_value || 0,
            'Addendum Weight (MT)':r.addendum_weight || 0,
            'Parent Contract $':   r.parent_contract_value || 0,
            'Signed By':           r.signed_by || '',
            'Released':            r.released ? 'Yes' : 'No',
            'Sales Unit':          r.unit_name || '',
            'Region':              r.region || '',
            'Sales Incharge':      r.sales_incharge || '',
            'Province':            r.pj_province || '',
            'Country':             r.pj_country || '',
          }))
        : allPages.map(r => ({
            'Contract #':     r.contract_number || '',
            'J Number':       r.j_number || r.q_number || '',
            'Project':        r.project_name || '',
            'Customer':       r.customer_name || '',
            'Contract Date':  r.contract_date || '',
            'Signed Date':    r.signed_date || '',
            'Contract Value': r.contract_value || 0,
            'Addendum Value': r.addendum_value || 0,
            'Total Value':    r.grand_total_value || 0,
            'Weight (MT)':    r.contract_weight || 0,
            'Addendums':      r.addendum_count || 0,
            'Released':       r.released ? 'Yes' : 'No',
            'Sales Unit':     r.unit_name || '',
            'Region':         r.region || '',
            'Type':           r.type_of_sales || '',
            'Sales Incharge': r.sales_incharge || '',
            'Province':       r.pj_province || '',
            'Country':        r.pj_country || ''
          }));

      ExportToExcel(exportRows, `${title.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}`);
    } catch {
      alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const contractColumnDefs = useMemo(() => [
    {
      headerName: 'J Number',
      field:      'j_number',
      width:      120,
      pinned:     'left',
      cellRenderer: (p) => {
        const num = p.data?.j_number || p.data?.q_number || '—';
        return p.data?.deal_id
          ? <Link to={`/crm/projects/${p.data.deal_id}`} target="_blank" rel="noreferrer" className="tw-font-mono tw-font-bold tw-text-blue-700 hover:tw-underline">{num}</Link>
          : num;
      }
    },
    { headerName: 'Contract #',    field: 'contract_number',  width: 130 },
    { headerName: 'Project',       field: 'project_name',     flex: 2, minWidth: 140 },
    { headerName: 'Customer',      field: 'customer_name',    flex: 1, minWidth: 120 },
    { headerName: 'Date',          field: 'contract_date',    width: 120, valueFormatter: p => fmtDate(p.value), cellStyle: { color: '#6B7280', fontSize: '11px' } },
    {
      headerName:    'Contract Value',
      field:         'contract_value',
      width:         120,
      type:          'numericColumn',
      valueFormatter: p => fmtM(p.value),
      cellStyle:     { textAlign: 'right', fontWeight: 600, color: '#1D4ED8' }
    },
    {
      headerName:    'Adm. Value',
      field:         'addendum_value',
      width:         110,
      type:          'numericColumn',
      valueFormatter: p => p.value !== 0 ? fmtM(p.value) : '—',
      cellStyle:     (p) => ({ textAlign: 'right', color: p.value < 0 ? '#DC2626' : '#D97706' })
    },
    {
      headerName:    'Total Value',
      field:         'grand_total_value',
      width:         110,
      type:          'numericColumn',
      valueFormatter: p => fmtM(p.value),
      cellStyle:     { textAlign: 'right', fontWeight: 700, color: '#059669' }
    },
    {
      headerName:    'Weight (MT)',
      field:         'contract_weight',
      width:         100,
      type:          'numericColumn',
      valueFormatter: p => `${Number(p.value || 0).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} MT`,
      cellStyle:     { textAlign: 'right' }
    },
    {
      headerName: 'Adm',
      field:      'addendum_count',
      width:      60,
      type:       'numericColumn',
      cellStyle:  (p) => ({ textAlign: 'center', color: p.value > 0 ? '#D97706' : '#9CA3AF', fontWeight: p.value > 0 ? 700 : 400 })
    },
    {
      headerName: 'Released',
      field:      'released',
      width:      90,
      cellRenderer: p => p.value
        ? <span className="tw-px-1.5 tw-py-0.5 tw-rounded-xl tw-text-xs tw-font-medium tw-bg-green-100 tw-text-green-700">Yes</span>
        : <span className="tw-px-1.5 tw-py-0.5 tw-rounded-xl tw-text-xs tw-font-medium tw-bg-amber-100 tw-text-amber-700">Pending</span>
    },
    { headerName: 'Region',   field: 'region',         width: 90,  cellStyle: { fontSize: '11px', color: '#6B7280' } },
    { headerName: 'Incharge', field: 'sales_incharge', width: 110, cellStyle: { fontSize: '11px' } },
    { headerName: 'Province', field: 'pj_province',    width: 100, cellStyle: { fontSize: '11px', color: '#6B7280' } },
    { headerName: 'Country',  field: 'pj_country',     width: 80,  cellStyle: { fontSize: '11px', color: '#6B7280' } }
  ], []);

  const addendumColumnDefs = useMemo(() => [
    {
      headerName: 'J Number',
      field:      'j_number',
      width:      120,
      pinned:     'left',
      cellRenderer: (p) => {
        const num = p.data?.j_number || p.data?.q_number || '—';
        return p.data?.deal_id
          ? <Link to={`/crm/projects/${p.data.deal_id}`} target="_blank" rel="noreferrer" className="tw-font-mono tw-font-bold tw-text-blue-700 hover:tw-underline">{num}</Link>
          : num;
      }
    },
    { headerName: 'Contract #',  field: 'contract_number',       width: 130 },
    { headerName: 'Addendum #',  field: 'addendum_number',       width: 130 },
    { headerName: 'Adm Date',    field: 'addendum_date',         width: 120, valueFormatter: p => fmtDate(p.value), cellStyle: { color: '#D97706', fontSize: '11px', fontWeight: 600 } },
    { headerName: 'Ctr Date',    field: 'contract_date',         width: 120, valueFormatter: p => fmtDate(p.value), cellStyle: { color: '#6B7280', fontSize: '11px' } },
    { headerName: 'Project',     field: 'project_name',          flex: 2, minWidth: 140 },
    { headerName: 'Customer',    field: 'customer_name',         flex: 1, minWidth: 120 },
    {
      headerName:    'Adm Value',
      field:         'addendum_value',
      width:         120,
      type:          'numericColumn',
      valueFormatter: p => fmtM(p.value),
      cellStyle:     (p) => ({ textAlign: 'right', fontWeight: 700, color: Number(p.value) < 0 ? '#DC2626' : '#D97706' })
    },
    {
      headerName:    'Adm Weight',
      field:         'addendum_weight',
      width:         105,
      type:          'numericColumn',
      valueFormatter: p => `${Number(p.value || 0).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} MT`,
      cellStyle:     (p) => ({ textAlign: 'right', color: Number(p.value) < 0 ? '#DC2626' : '#374151' })
    },
    {
      headerName:    'Parent Ctr $',
      field:         'parent_contract_value',
      width:         110,
      type:          'numericColumn',
      valueFormatter: p => fmtM(p.value),
      cellStyle:     { textAlign: 'right', color: '#1D4ED8' }
    },
    { headerName: 'Signed By', field: 'signed_by', width: 110, cellStyle: { fontSize: '11px' } },
    {
      headerName: 'Released',
      field:      'released',
      width:      90,
      cellRenderer: p => p.value
        ? <span className="tw-px-1.5 tw-py-0.5 tw-rounded-xl tw-text-xs tw-font-medium tw-bg-green-100 tw-text-green-700">Yes</span>
        : <span className="tw-px-1.5 tw-py-0.5 tw-rounded-xl tw-text-xs tw-font-medium tw-bg-amber-100 tw-text-amber-700">Pending</span>
    },
    { headerName: 'Unit',     field: 'unit_name',      width: 90,  cellStyle: { fontSize: '11px' } },
    { headerName: 'Region',   field: 'region',         width: 90,  cellStyle: { fontSize: '11px', color: '#6B7280' } },
    { headerName: 'Incharge', field: 'sales_incharge', width: 110, cellStyle: { fontSize: '11px' } },
    { headerName: 'Province', field: 'pj_province',    width: 100, cellStyle: { fontSize: '11px', color: '#6B7280' } },
    { headerName: 'Country',  field: 'pj_country',     width: 80,  cellStyle: { fontSize: '11px', color: '#6B7280' } }
  ], []);

  const columnDefs = isAddendum ? addendumColumnDefs : contractColumnDefs;

  const drawerTitle = (
    <div className="tw-flex tw-items-center tw-justify-between tw-w-full tw-pr-2">
      <div className="tw-flex tw-items-center tw-gap-2">
        <span className="tw-font-bold tw-text-slate-800">{title}</span>
        {isAddendum && (
          <span className="tw-px-2 tw-py-0.5 tw-rounded-xl tw-text-xs tw-font-semibold tw-bg-amber-100 tw-text-amber-700">Addendums</span>
        )}
        {!loading && totalRecords > 0 && (
          <span className="tw-text-xs tw-text-slate-500 tw-bg-slate-100 tw-px-2 tw-py-0.5 tw-rounded-xl">
            {totalRecords.toLocaleString()} records
          </span>
        )}
      </div>
      <Button
        variant="secondary"
        size="sm"
        leftIcon={FiDownload}
        loading={exporting}
        disabled={loading || totalRecords === 0}
        onClick={handleExport}
        className="tw-border-emerald-300 tw-bg-emerald-50 tw-text-emerald-700 hover:tw-bg-emerald-100"
      >
        {exporting ? 'Exporting…' : 'Export Excel'}
      </Button>
    </div>
  );

  const summaryBar = totals ? (
    <div className="tw-flex tw-flex-wrap tw-gap-3 tw-px-1 tw-py-2 tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded">
      {!isAddendum && totals.contract_value != null && (
        <div className="tw-flex tw-items-center tw-gap-1.5">
          <span className="tw-text-xs tw-text-gray-500">Contract:</span>
          <span className="tw-text-xs tw-font-bold tw-text-blue-700">{fmtM(totals.contract_value)}</span>
        </div>
      )}
      {!isAddendum && totals.addendum_value != null && (
        <div className="tw-flex tw-items-center tw-gap-1.5">
          <span className="tw-text-xs tw-text-gray-500">Addendum:</span>
          <span className="tw-text-xs tw-font-bold tw-text-amber-600">{fmtM(totals.addendum_value)}</span>
        </div>
      )}
      {!isAddendum && totals.grand_total_value != null && (
        <div className="tw-flex tw-items-center tw-gap-1.5">
          <span className="tw-text-xs tw-text-gray-500">Total:</span>
          <span className="tw-text-xs tw-font-bold tw-text-emerald-700">{fmtM(totals.grand_total_value)}</span>
        </div>
      )}
      {!isAddendum && totals.contract_weight != null && (
        <div className="tw-flex tw-items-center tw-gap-1.5">
          <span className="tw-text-xs tw-text-gray-500">Weight:</span>
          <span className="tw-text-xs tw-font-bold tw-text-gray-700">{fmtW(totals.contract_weight)}</span>
        </div>
      )}
      {isAddendum && totals.addendum_value != null && (
        <div className="tw-flex tw-items-center tw-gap-1.5">
          <span className="tw-text-xs tw-text-gray-500">Total Adm Value:</span>
          <span className="tw-text-xs tw-font-bold tw-text-amber-600">{fmtM(totals.addendum_value)}</span>
        </div>
      )}
      {isAddendum && totals.addendum_weight != null && (
        <div className="tw-flex tw-items-center tw-gap-1.5">
          <span className="tw-text-xs tw-text-gray-500">Total Adm Weight:</span>
          <span className="tw-text-xs tw-font-bold tw-text-gray-700">{fmtW(totals.addendum_weight)}</span>
        </div>
      )}
    </div>
  ) : null;

  const footer = (summaryBar || totalPages > 1) ? (
    <div className="tw-flex tw-flex-col tw-gap-2">
      {summaryBar}
      {totalPages > 1 && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          totalResults={totalRecords}
          perPage={PAGE_SIZE}
          onPageChange={(pg) => { setPage(pg); fetchRecords(pg); }}
          disabled={loading}
        />
      )}
    </div>
  ) : null;

  return (
    <Drawer show={open} onHide={onClose} title={drawerTitle} footer={footer} width="900px" minWidth="340px">
      <AgGridTable
        rowData={records}
        columnDefs={columnDefs}
        loading={loading}
        pagination={false}
        filter={false}
        sort={false}
        rowHeight={36}
        headerHeight={38}
        fontSize="12px"
        height="max(320px, calc(100dvh - 180px))"
        enableCellTextSelection
      />
    </Drawer>
  );
};

ContractDrillDownPanel.propTypes = {
  open:        PropTypes.bool.isRequired,
  title:       PropTypes.string.isRequired,
  baseParams:  PropTypes.object.isRequired,
  extraParams: PropTypes.object,
  onClose:     PropTypes.func.isRequired,
  mode:        PropTypes.oneOf(['contracts', 'addendums'])
};
ContractDrillDownPanel.defaultProps = { extraParams: {}, mode: 'contracts' };

export default ContractDrillDownPanel;
